import {
  betStore,
  popupStore,
  userInfoStore,
  roundInfoStore,
  gameResultStore,
  gameConfigStore,
  profileAvatarStore,
  loadingStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  toFixed,
  BetStatus,
  RoundStatus,
  freeBetStore,
  notificationStore,
  parseNumberByPrecision,
} from '@pascal/pascal-core';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';

import { worker } from '../index';
import { liveConnection } from '../core';
import { gameStore } from '../store';
import { wheelDeviation } from '../store/gameStore';
import { bonusIndexes, WheelTypes } from '../enums';
import { doBetStore, candyDataStore } from '../store';
import { gameLiveConnection } from './GameLiveConnection';
import { soundService, soundTypes } from './soundService';

let timeOutId;

export const wheelDefaultAnimationDuration = 4000;
const defaultZoomAnimDelay = 2000;
const defaultZoomAnimDuration = 4000;

class GameService {
  onGetInitialStateResult({
    rate,
    limits,
    results,
    balance,
    avatarId,
    precision,
    roundInfo,
    currencyCode,
    currentBets,
    gameConfiguration,
  }) {
    const { OnHold, Started, OnFinish, Finished } = RoundStatus;
    const { status, second, betsClosedDuration } = roundInfo;
    doBetStore.setWinAmount('');
    soundService.stopAll();
    betStore.setLiveBets([]);
    gameStore.setIsLoadingPage(true);
    candyDataStore.resetZoomAnim();
    candyDataStore.resetGameAnim();
    candyDataStore.resetReBetData();
    candyDataStore.resetCandiesGameData();
    betStore.resetBetHistory();
    notificationStore.setNotificationType(null);

    gameStore.setBetsClosedDuration(betsClosedDuration);
    if (second < 0 || [])
      doBetStore.setActiveButton(second < 0 || [OnHold, OnFinish, Finished].includes(status));
    gameStore.setGameState({
      status,
      isInitialLoaded: true,
    });

    const currentCandyRound = results.find(
      (item) => item.status === OnFinish || item.status === OnHold
    );

    if (currentCandyRound) {
      const { wheelState, multiplierCandy, multiplierOdd, wheelIndexes } =
        currentCandyRound?.roundData;

      if (wheelState === WheelTypes.first) {
        candyDataStore.setMultiplierData({ multiplierCandy, multiplierOdd });
      }
      candyDataStore.setNextWheelType(wheelIndexes[WheelTypes.first] + wheelDeviation);
      candyDataStore.setCurrentWheelType(wheelState);
      candyDataStore.setGameResult({
        wheelResult: { index: wheelIndexes[wheelState] },
      });
      candyDataStore.setResultIndex(wheelIndexes[wheelState]);
      document.documentElement.style.setProperty(
        '--wheelCurrentIndex',
        `${wheelIndexes[wheelState] + wheelDeviation}`
      );
      candyDataStore.callDecidedAnimation({ second, betsClosedDuration });
    }

    if (status === OnHold) {
      gameStore.setIsBetsClosed(true);
    } else if (status === Started) {
      const isBetsClosed = second < 0;
      doBetStore.setActiveButton(isBetsClosed);
    }

    candyDataStore.setRtpType(gameConfiguration?.return);

    if (currentBets && currentBets.length) {
      betStore.setLiveBets(currentBets);
    }
    gameResultStore.setGameResults(results);
    profileAvatarStore.updateAvatarId(avatarId);
    profileAvatarStore.updateInitialAvatarId(avatarId);
    roundInfoStore.setRoundInfo(roundInfo);

    gameConfigStore.setLimits(limits);

    if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      gameStore.setTimerDuration(roundInfo.second);
      const time = Math.floor(Math.abs(roundInfo.second));
      gameStore.setTime(time);
      this.sendWorker(time);
    } else {
      this.sendWorker();
      gameStore.setTimerDuration(roundInfo.duration);
      gameStore.setTime(null);
    }

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);

    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    if (Number(userId) === Number(+data.userId)) {
      // const isAlreadyBetAccepted = candyDataStore.candiesBetData.some((item) => item.betId);
      //
      // if (!isAlreadyBetAccepted) {
      //   const { isSoundOn } = userSettingsStore;
      //
      //   if (isSoundOn) {
      //     soundService.playBetAccepted();
      //   }
      // }
      soundService.playBetAccepted();
      if (data?.freeBetId) {
        data?.betData?.forEach(() => {
          freeBetStore.handleFreeBetDoBetResult(data.freeBetId);
        });
      }
      doBetStore.handleDoBetResult(data, candyDataStore.candiesBetData);
    }

    betStore.updateLiveBets(data);
  }

  handleDoBetResultValidation(data) {
    const { responseCode } = data;
    candyDataStore.resetCandiesData();
    candyDataStore.resetReBetData();
    notificationStore.setNotificationType({ responseCode });
  }

  handleRoundStart({ data: { startedRound, results } }) {
    const { nextWheelType } = candyDataStore;
    this.sendWorker(roundInfoStore.duration);
    gameStore.setIsLiveBetsLoader(true);
    gameStore.setGameState({
      status: RoundStatus.Started,
      isInitialLoaded: true,
    });
    candyDataStore.resetCandiesGameData();
    betStore.setLiveBets([]);
    candyDataStore.setCurrentWheelType(WheelTypes.first);
    roundInfoStore.setRoundId(startedRound.roundId);
    gameStore.setTimerDuration(startedRound.duration);
    doBetStore.setActiveButton(false);
    gameStore.setIsBetsClosed(false);
    gameResultStore.setGameResults(results);
    freeBetStore.handleFreeBetEndedData();

    if (nextWheelType) {
      document.documentElement.style.setProperty('--wheelCurrentIndex', `${nextWheelType}`);
    }
    // gameLiveConnection.getBalance();
  }

  handleResultReport({ data: { resultReportData } }) {
    const updatedDuration = wheelDefaultAnimationDuration;
    const updatedStarted = 0;
    const { isSoundOn } = userSettingsStore;
    const { wheel, wheelResult } = resultReportData;
    const { index } = wheelResult;

    switch (wheel) {
      case WheelTypes.first:
        const { multiplierCandy } = resultReportData;
        candyDataStore.setMultiplierData(multiplierCandy);
        candyDataStore.setGameResult({ wheelResult });
        candyDataStore.setCurrentWheelType(WheelTypes.first);
        candyDataStore.setNextWheelType(index + wheelDeviation);
        document.documentElement.style.setProperty(
          '--wheelCurrentIndex',
          `${index + wheelDeviation}`
        );
        break;
      default:
        candyDataStore.setResultIndex(null);
        candyDataStore.setGameResult({ wheelResult });
        candyDataStore.setCurrentWheelType(wheel);
        document.documentElement.style.setProperty(
          '--wheelCurrentIndex',
          `${index + wheelDeviation}`
        );
    }
    setTimeout(() => {
      candyDataStore.setResultIndex(null);
      candyDataStore.setGameAnim({
        animationDelay: `${updatedStarted}ms`,
        animationDuration: `${updatedDuration}ms`,
      });
      if (wheel === WheelTypes.second) {
        candyDataStore.setGameResult({ wheelResult });
        candyDataStore.setCurrentWheelType(WheelTypes.second);
        document.documentElement.style.setProperty(
          '--wheelCurrentIndex',
          `${index + wheelDeviation - 5}`
        );
      }
      isSoundOn && soundService.play(soundTypes.Wheel, Math.abs(updatedStarted / 1000));
      setTimeout(() => {
        candyDataStore.setZoomAnim({
          animationDelay: `${defaultZoomAnimDelay}ms`,
          animationDuration: `${defaultZoomAnimDuration}ms`,
        });
      }, 650);
    }, 750);

    setTimeout(() => {
      candyDataStore.setResultIndex(index);
    }, 4250);
    setTimeout(() => {
      candyDataStore.resetGameAnim();
      if (index) {
        if (bonusIndexes[candyDataStore?.currentWheelType]?.includes(index)) {
          candyDataStore.setCurrentWheelType(candyDataStore.currentWheelType + 1);
        }
      }
    }, updatedDuration + 750);
  }

  handleRoundFin({ data }) {
    const { bets } = data;
    const { userId } = userInfoStore;
    // const { limits } = gameConfigStore;
    const { repliedBetData } = betStore;
    // let finishedDemoBet;
    let userWinning = 0;

    // if (isDemo) {
    //   const demoBet = liveBets.find((bet) => !bet?.playerExternalId);
    //   const { candy, multiplierCandy, multiplierOdd, odd } = roundData;
    //
    //   if (demoBet) {
    //     finishedDemoBet = {
    //       ...demoBet,
    //       status: null,
    //       betData: demoBet?.betData?.map((bet) => {
    //         let winAmount = 0;
    //
    //         if (bet.candy === candy) {
    //           bet.odd = multiplierCandy === bet.candy ? multiplierOdd : odd;
    //           winAmount = toFixed(multiply(bet.odd, bet.amount), 2);
    //
    //           userWinning += winAmount;
    //         } else {
    //           bet.odd = 0;
    //         }
    //
    //         return {
    //           ...bet,
    //           winAmount,
    //           status: bet.candy === candy ? BetStatus.Win : BetStatus.Lose,
    //         };
    //       }),
    //     };
    //     const demoWinAmount = (
    //       finishedDemoBet?.betData || finishedDemoBet?.additionalParams
    //     )?.reduce((acc, curr) => plus(acc, curr.winAmount), 0);
    //     if (demoWinAmount) {
    //       userSettingsStore.isSoundOn && soundService.playWin();
    //       const currentDemoWinAmount =
    //         demoWinAmount > limits.maxWin ? limits.maxWin : demoWinAmount;
    //       if (demoWinAmount > limits.maxWin) {
    //         notificationStore.setNotificationType({ responseCode: BetResponseCode.MaxWinPayout });
    //       }
    //       doBetStore.setWinAmount(currentDemoWinAmount);
    //       doBetStore.increaseDemoBalance(currentDemoWinAmount);
    //       setTimeout(() => doBetStore.setWinAmount(''), 2000);
    //     }
    //     betStore.updateDemoBetHistory(
    //       (finishedDemoBet?.betData || finishedDemoBet?.additionalParams)?.map((candyData) => ({
    //         ...finishedDemoBet,
    //         ...candyData,
    //       }))
    //     );
    //   }
    // } else {
    const userBet = bets.find((item) => item.userId === +userId);
    if (userBet) {
      const forWinningCalcData = userBet?.additionalParams || userBet?.betData;
      (userBet?.additionalParams || userBet?.betData)?.forEach((item) => {
        item?.status === BetStatus.Win &&
          freeBetStore.freeBetTotalWinCalculator({ ...userBet, ...item });
      });
      userWinning = forWinningCalcData.reduce(
        (acc, bet) => parseNumberByPrecision(1, acc + bet?.winAmount || 0),
        0
      );
    }
    (repliedBetData ? repliedBetData.winAmount : userWinning) &&
      userSettingsStore.isSoundOn &&
      soundService.playWin();
    doBetStore.setWinAmount(toFixed(repliedBetData ? repliedBetData.winAmount : userWinning, 2));

    setTimeout(() => doBetStore.setWinAmount(''), 2000);
    // }
    gameStore.setGameState({ status: RoundStatus.Finished, second: null, isInitialLoaded: false });
    betStore.setLiveBets([...bets]);
  }

  handleBetsClosed() {
    doBetStore.setActiveButton(true);
    gameStore.setIsBetsClosed(true);
    gameStore.setIsLiveBetsLoader(false);
    const { roundId } = roundInfoStore;
    const { activeFreeBet } = freeBetStore;
    // const {
    //   limits: { minBet },
    // } = gameConfigStore;
    // const { balance } = userInfoStore;
    const { candiesBetData } = candyDataStore;

    const candiesDataWithAmount = candiesBetData.filter((item) => item.amount);
    // const totalBetAmount = candiesDataWithAmount.reduce((acc, curr) => acc + curr.amount, 0);
    if (candiesDataWithAmount && candiesDataWithAmount?.length) {
      const doBetModel = {
        roundId,
        ...(activeFreeBet?.id
          ? { freeBetId: activeFreeBet?.id, amount: activeFreeBet?.amount }
          : {}),
        betData: candiesDataWithAmount.map((item) => ({
          amount: activeFreeBet?.id ? activeFreeBet.amount : item.amount,
          candy: item.index,
        })),
      };

      // if (isDemo) {
      //   const currentDemoBetBet = {
      //     ...doBetModel,
      //     status: BetStatus.Pending,
      //     id: nanoid(),
      //     date: new Date(),
      //     responseCode: BetResponseCode.Success,
      //   };
      //   if (+balance >= +totalBetAmount) {
      //     if (+totalBetAmount < minBet) {
      //       notificationStore.setNotificationType({ responseCode: BetResponseCode.InValidMinBet });
      //       candyDataStore.resetReBetData();
      //     } else {
      //       doBetStore.handleDoBetResult(currentDemoBetBet, candiesBetData);
      //     }
      //   } else {
      //     notificationStore.setNotificationType({
      //       responseCode: BetResponseCode.InsufficientFunds,
      //     });
      //     candyDataStore.resetReBetData();
      //   }
      // } else {
      gameLiveConnection.doBet(doBetModel);
      // }
    }
    freeBetStore.handleFreeBetEndedData();
  }

  countDown({ data: time }) {
    gameStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError(e) {
    console.error(e, 'error onClose');
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
